import React, { useEffect, useState } from "react";
import axios from "axios";
import { ArrowRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(d) {
  try {
    return new Date(d).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

const BlogCard = ({ post, onRead }) => (
  <article
    data-testid={`blog-card-${post.id}`}
    className="group rounded-2xl overflow-hidden bg-white border border-[var(--gg-border)] hover:shadow-xl hover:-translate-y-1 transition-all"
  >
    {post.cover_image_url && (
      <div className="relative aspect-[16/10] overflow-hidden">
        <img
          src={post.cover_image_url}
          alt={post.title}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-[1.04]"
          loading="lazy"
        />
      </div>
    )}
    <div className="p-7">
      <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.22em] text-[var(--gg-primary)] font-semibold">
        <span>{post.author}</span>
        <span className="w-1 h-1 rounded-full bg-[var(--gg-primary)]/50" />
        <span>{fmtDate(post.created_at)}</span>
      </div>
      <h3 className="font-serif text-2xl text-[var(--gg-deep)] mt-3 font-semibold leading-snug">
        {post.title}
      </h3>
      <p className="text-sm text-[var(--gg-text)] leading-relaxed mt-3">
        {post.excerpt}
      </p>
      <button
        onClick={() => onRead(post)}
        data-testid={`blog-read-${post.id}`}
        className="mt-6 inline-flex items-center gap-2 text-sm text-[var(--gg-primary)] hover:text-[var(--gg-primary-dark)] font-semibold group/btn"
      >
        Read more
        <ArrowRight
          size={15}
          className="transition-transform group-hover/btn:translate-x-1"
        />
      </button>
    </div>
  </article>
);

const BlogPosts = ({ loading, posts, onRead }) => {
  if (loading) {
    return (
      <div data-testid="blog-loading" className="text-sm text-[var(--gg-text-muted)]">
        Loading posts…
      </div>
    );
  }
  if (posts.length === 0) {
    return (
      <div
        data-testid="blog-empty"
        className="rounded-2xl border border-dashed border-[var(--gg-border)] bg-white p-10 text-center"
      >
        <p className="text-[var(--gg-text)] font-medium">
          Our first stories are on their way.
        </p>
        <p className="text-sm text-[var(--gg-text-muted)] mt-2">
          Subscribe below and we&apos;ll send new Graceful Blog posts
          straight to your inbox.
        </p>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
      {posts.map((p) => (
        <BlogCard key={p.id} post={p} onRead={onRead} />
      ))}
    </div>
  );
};

const BlogPostDialog = ({ post, onClose }) => (
  <Dialog open={!!post} onOpenChange={(v) => !v && onClose()}>
    <DialogContent
      data-testid="blog-dialog"
      className="max-w-2xl bg-white max-h-[85vh] overflow-y-auto"
    >
      {post && (
        <>
          <DialogHeader>
            <span className="text-[10px] tracking-[0.25em] uppercase text-[var(--gg-primary)] font-semibold">
              {post.author} · {fmtDate(post.created_at)}
            </span>
            <DialogTitle className="font-serif text-3xl text-[var(--gg-deep)] mt-2 leading-tight">
              {post.title}
            </DialogTitle>
            <DialogDescription className="text-[var(--gg-text-muted)] pt-1">
              {post.excerpt}
            </DialogDescription>
          </DialogHeader>
          {post.cover_image_url && (
            <img
              src={post.cover_image_url}
              alt={post.title}
              className="w-full rounded-xl mt-2"
            />
          )}
          <div className="prose prose-sm max-w-none text-[var(--gg-text)] leading-relaxed mt-2 whitespace-pre-wrap">
            {post.body}
          </div>
        </>
      )}
    </DialogContent>
  </Dialog>
);

export default function BlogSection() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchPosts = async () => {
      try {
        const { data } = await axios.get(`${API}/blog?limit=9`);
        if (!cancelled) setPosts(data || []);
      } catch {
        if (!cancelled) setPosts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchPosts();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section
      id="blog"
      data-testid="blog-section"
      className="py-24 md:py-28 bg-[var(--gg-surface)]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
          <div className="max-w-2xl">
            <span className="eyebrow">Graceful Blog</span>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-[54px] text-[var(--gg-deep)] mt-4 leading-[1.05] font-semibold text-balance">
              Notes on care, home,
              <br />
              <em className="not-italic text-[var(--gg-primary)] font-normal">
                and coming back to Jamaica.
              </em>
            </h2>
          </div>
          <p className="text-[var(--gg-text)] leading-relaxed max-w-sm">
            Practical guidance and quiet stories from our team — for seniors,
            returning residents, and the families who love them.
          </p>
        </div>

        <BlogPosts loading={loading} posts={posts} onRead={setOpen} />
      </div>

      <BlogPostDialog post={open} onClose={() => setOpen(null)} />
    </section>
  );
}
